import { createSlice } from '@reduxjs/toolkit';

import { PreviewType } from 'editor/src/store/editorModules/preview/types';
import { WarningType } from 'editor/src/store/editorModules/warnings/types';

import environment from 'editor/src/environment';

import setHelpCenterArticlesReducer from './reducer/setHelpCenterArticlesReducer';
import setHelpCenterArticlesSearchStringReducer from './reducer/setHelpCenterArticlesSearchStringReducer';
import setHostSettingsReducer from './reducer/setHostSettingsReducer';
import { HostSettingsState } from './types';

export const DEFAULT_LOW_DPI = 150;
export const DEFAULT_MEDIUM_DPI = 300;

export const hostSettingsInitialState: HostSettingsState = {
  showDoubleLine: true,
  linesColor: { main: 'rgba(51, 51, 51, 1)', outer: 'rgba(255,255,255,0.7)' },
  showPrintAreaLines: true,
  dpiLevels: { showHighToastr: false, showMediumToastr: false },
  changeImageFrameColorBasedOnDPI: true,
  backgroundColor: undefined,
  showCanvasOperationHelper: false,

  // wallpaper
  allowShowRollerStripesChange: false,
  allowShowRulersChange: false,
  allowShowPerspectiveLines: false,
  activateWallpaperEmptyScreen: false,
  resizeProductBasedOnFirstElement: false,
  repeatDesign: undefined,

  allowAddElements: true,
  allowRemoveElements: true,
  allowUseNotUploadedImages: true,
  autoApplyUploadedImages: false,
  allowLockElements: true,
  allowPersonalizationLock: false,
  allowSampleFlagChange: false,
  flagNewTextElementsAsSample: false,
  allowStaticFlagChange: false,
  allowSpreadBackgroundColorChange: false,
  ignorePersonalizationLock: true,
  allowAutofill: false,
  allowSelectMultipleImages: false,
  allowLockedImageElementDeletion: true,
  defaultPersonalizationLock: true,
  deleteFrameWithImage: false,
  splitDisplayPerContent: false,
  enableMirroring: false,
  enableGraphicStudioDiscovery: false,
  hostControlsMirroring: false,
  hideAddonsSearch: false,
  editImageFlag: false,
  enableCalendarEvents: false,
  showCroppedImageArea: false,
  enableHelpCenter: undefined,
  enableImageShadow: false,
  enableMockupLeaning: false,
  enableSwitchDesignTemplates: false,
  enablePreflightGeneratedProductMockupImage: false,
  showDesignScroll: false,
  enableProductFitting: true,
  digitizationRules: {
    maxColorsCount: 6,
    // unused while enabledWarnings.MaxStitchCount is false
    // on create side we use 10000 as per GAPI-26910
    maxStitchCount: 15000,
  },
  previews: {
    includeBackground: true,
    includeForegound: false,
    firstSpreadOnly: false,
    noFlatPreviews: true,
    clientSideRendering: false,
  },
  perSpreadPreviewAvailable: false,
  hidePreviewButton: false,

  coreFeatures: {
    imageCrop: true,
    imageCropRotate: true,
    imageCropZoom: true,
    imageFlip: true,
    imagePerspectiveTransform: false,
  },

  enabledWarnings: {
    [WarningType.LowDPI]: true,
    [WarningType.NotVisible]: true,
    [WarningType.OutOfRange]: true,
    // once you want to enable MaxStitchCount, set it to true and update maxStitchCount in digitizationRules to some level, as 15k may be too high for some designs
    [WarningType.MaxStitchCount]: false,
    [WarningType.MaxColorCount]: false,
    [WarningType.EmptyPage]: false,
    [WarningType.EmptyDesign]: false,
    [WarningType.EmptyImageBox]: false,
    [WarningType.SampleElementNotReplaced]: true,
    [WarningType.DtfImageBackground]: true,
    [WarningType.WhiteUnderbase]: true,
    [WarningType.MaxCharCountReached]: true,
  },
  showWarningsOnlyAfterDesignRequest: false,
  hiddenWarnings: {},
  colorPickerConfig: {
    enableOpacity: false,
  },
  minFontSize: 1,
  maxFontSize: 2000,
  showEmptyDesignPlaceholder: false,
  defaultElementPositioning: 'fill',

  ignoreDesignPlugins: false,
  designOptions: false,
  enableSpreadsView: false,
  enableEditorHeader: false,
  enableManualSaveDraft: false,
  variantsLimit: undefined,
  defaultPersonalizationPlugin: false,
  allowPrintFileUpload: false,
  printFileUploadEnabled: false,
  editorMode: undefined,
  allowCurvedText: false,
  variantSwitchConfirmationEnabled: false,
  allowMultipleColorSpaces: false,
  enableImagesRenaming: false,
  enableInDesignImport: false,
  previewType: PreviewType.PREVIEW_FLAT,
  hostActions: undefined,

  ...environment.hostSettingsOverrides,
};

const slice = createSlice({
  name: 'hostSettings',
  initialState: hostSettingsInitialState,
  reducers: {
    setHostSettingsAction: setHostSettingsReducer,
    setHelpCenterArticlesAction: setHelpCenterArticlesReducer,
    setHelpCenterArticlesSearchStringAction: setHelpCenterArticlesSearchStringReducer,
  },
});

const { actions, reducer } = slice;

export const { setHostSettingsAction, setHelpCenterArticlesAction, setHelpCenterArticlesSearchStringAction } = actions;

export default reducer;
